// Tavern → combat FIGHT intro — the tape splice visuals.
//
// The transport halts, splice ticks flash at the cut, two takes slam inward and
// meet, and the seam parts outward onto the arena. See `theme/spliceTimeline.ts`
// for the score and the reasoning behind its length.
//
// WHY A SPLICE SOLVES THE HARD PROBLEM HERE
// -----------------------------------------
// The tavern is DOM; combat is a PIXI canvas. Animating tavern units into canvas
// space would mean tweening across a renderer boundary — the one genuinely hard
// technical question in this transition. A splice sidesteps it entirely: a cut
// is a full-bleed cover by nature, so the two halves hide the boundary the same
// way the transport's bands hide a route swap. Continuity is carried by the
// *cut*, not by moving objects across renderers.
//
// Painted imperatively off the shared clock, for the reasons documented in
// TapeTransportRenderer.

import { useRef } from 'react';
import { useTransitionStore } from '../../stores/transition';
import type { SpliceScore } from '../../theme/spliceTimeline';
import { buildSpliceTracks, type SpliceTracks } from '../../theme/spliceTracks';
import { beatEnd, sampleTrack } from '../../theme/sequence';
import { colors } from '../../theme/tokens';
import { useSequenceFrame } from './clock';

export function SpliceRenderer({ score }: { score: SpliceScore }) {
  const left = useRef<HTMLDivElement | null>(null);
  const right = useRef<HTMLDivElement | null>(null);
  const seam = useRef<HTMLDivElement | null>(null);
  const marks = useRef<HTMLDivElement | null>(null);
  const flash = useRef<HTMLDivElement | null>(null);
  const crews = useRef<HTMLDivElement | null>(null);

  const tracks = useRef<SpliceTracks | null>(null);
  if (!tracks.current || tracks.current.forTotal !== score.total) {
    tracks.current = buildSpliceTracks(score);
  }

  // Crew names come from the store's payload rather than the match store, so
  // this renderer stays ignorant of game state.
  const { you, them } = useTransitionStore.getState().payload;

  useSequenceFrame((elapsed) => {
    const tr = tracks.current;
    if (!tr) return;

    const shut = sampleTrack(tr.shut, elapsed);
    const dissolve = sampleTrack(tr.dissolve, elapsed);

    // Judder — a couple of px of horizontal jitter on the freeze, so the stop
    // reads as a mechanism jamming rather than a fade beginning. Deterministic
    // per frame index so it can't shimmer differently between runs.
    let jitter = 0;
    if (score.judder > 0 && elapsed < beatEnd(score.stop)) {
      const f = Math.floor(elapsed * 60);
      jitter = ((f * 2654435761) % 100) / 100 - 0.5;
      jitter *= score.judder;
    }

    if (left.current) {
      left.current.style.transform = score.travels
        ? `translateX(${-100 + shut * 100}%)`
        : 'translateX(0%)';
      left.current.style.opacity = String(score.travels ? 1 : dissolve);
    }
    if (right.current) {
      right.current.style.transform = score.travels
        ? `translateX(${100 - shut * 100}%)`
        : 'translateX(0%)';
      right.current.style.opacity = String(score.travels ? 1 : dissolve);
    }
    if (seam.current) {
      seam.current.style.opacity = String(sampleTrack(tr.seam, elapsed));
      seam.current.style.transform = `translateX(${jitter}px) scaleY(${sampleTrack(tr.seamScale, elapsed)})`;
    }
    if (marks.current) {
      marks.current.style.opacity = String(sampleTrack(tr.marks, elapsed));
    }
    if (flash.current) {
      flash.current.style.opacity = String(sampleTrack(tr.flash, elapsed));
    }
    if (crews.current) {
      crews.current.style.opacity = String(sampleTrack(tr.crews, elapsed));
    }
  });

  return (
    <>
      {/* The two takes. Full-height halves that slam inward and meet at the
          seam, then part outward onto the arena. */}
      <div
        ref={left}
        className="absolute inset-y-0 left-0 w-1/2"
        style={{ backgroundColor: colors.void, transform: 'translateX(-100%)' }}
      >
        <div
          className="absolute inset-y-0 right-0 w-[2px]"
          style={{ backgroundColor: colors.sodium, opacity: 0.6 }}
        />
      </div>
      <div
        ref={right}
        className="absolute inset-y-0 right-0 w-1/2"
        style={{ backgroundColor: colors.void, transform: 'translateX(100%)' }}
      >
        <div
          className="absolute inset-y-0 left-0 w-[2px]"
          style={{ backgroundColor: colors.sodium, opacity: 0.6 }}
        />
      </div>

      {/* The cut itself — a hot vertical line where the takes meet. */}
      <div className="absolute inset-y-0 left-1/2 -ml-[1.5px] w-[3px]">
        <div
          ref={seam}
          className="h-full w-full"
          style={{
            background: `linear-gradient(180deg, transparent, ${colors.sodiumGlow}, transparent)`,
            opacity: 0,
            transformOrigin: 'center',
          }}
        />
      </div>

      {/* Splice ticks — the one ornament, and the first thing familiarity
          drops. Editor's marks either side of the cut. */}
      {score.markCount > 0 && (
        <div
          ref={marks}
          className="absolute inset-y-0 left-1/2 flex -translate-x-1/2 flex-col items-center justify-center gap-[6px]"
          style={{ opacity: 0 }}
        >
          {Array.from({ length: score.markCount }).map((_, i) => (
            <span
              key={i}
              className="block h-[2px] w-[26px]"
              style={{
                backgroundColor: i % 2 === 0 ? colors.sodium : colors.marker,
                opacity: 0.75,
              }}
            />
          ))}
        </div>
      )}

      {/* Who is fighting. Reads during the closed beat — the only moment in the
          round where there is room to say it. */}
      <div
        ref={crews}
        className="absolute inset-0 flex items-center justify-center gap-lg px-lg"
        style={{ opacity: 0 }}
      >
        <span className="flex-1 text-right font-cholo text-[18px] leading-none text-cholo md:text-[34px] lg:text-[46px]">
          {you || 'your crew'}
        </span>
        <span className="font-data text-[10px] uppercase tracking-[0.4em] text-sodium md:text-[14px]">
          vs.
        </span>
        <span className="flex-1 font-emo text-[18px] leading-none text-emo md:text-[34px] lg:text-[46px]">
          {them || 'their crew'}
        </span>
      </div>

      {/* Impact flash on the meeting frame. */}
      <div
        ref={flash}
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at 50% 50%, rgba(255,176,74,0.55) 0%, rgba(255,138,61,0) 58%)',
          opacity: 0,
        }}
      />
    </>
  );
}
